import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Paper,
  Stack
} from '@mui/material';

const DynamicFormRenderer = ({ formSchema, formData, setFormData, sectionFilter, readOnly = false }) => {
  // Parse schema if it's a string
  const schema = typeof formSchema === 'string' ? JSON.parse(formSchema) : formSchema;

  const handleFieldChange = (fieldId, value) => {
    if (readOnly) return;
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  // Filter sections based on sectionFilter (e.g., 'employee', 'manager', 'second_level_manager')
  const sectionsToRender = schema.sections?.filter(section => {
    if (!sectionFilter) return true;
    return section.completedBy === sectionFilter || section.sectionId === sectionFilter;
  }) || [];

  return (
    <Box>
      {sectionsToRender.map((section, sectionIndex) => (
        <Box key={section.sectionId}>
          <Stack spacing={3}>
            {section.fields?.map((field, fieldIndex) => {
              const fieldValue = formData[field.fieldId] || '';

              // Heading field (for grouping questions)
              if (field.fieldType === 'heading') {
                // Find all fields that should be grouped under this heading
                const groupedFields = section.fields.filter(f => f.groupUnder === field.fieldId);
                
                return (
                  <Box key={field.fieldId} sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      {field.fieldLabel}
                    </Typography>
                    <Stack spacing={2} sx={{ ml: 2 }}>
                      {groupedFields.map(groupedField => {
                        const groupedValue = formData[groupedField.fieldId] || '';
                        
                        // Render grouped radio fields
                        if (groupedField.fieldType === 'radio') {
                          return (
                            <FormControl key={groupedField.fieldId} component="fieldset" required={groupedField.required}>
                              <FormLabel component="legend" sx={{ mb: 1, color: 'text.primary', fontWeight: 500, whiteSpace: 'pre-line' }}>
                                {groupedField.fieldLabel}
                              </FormLabel>
                              {readOnly ? (
                                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                                  {groupedValue || 'No response'}
                                </Typography>
                              ) : (
                                <RadioGroup
                                  value={groupedValue}
                                  onChange={(e) => handleFieldChange(groupedField.fieldId, e.target.value)}
                                >
                                  {groupedField.options?.map((option) => (
                                    <FormControlLabel
                                      key={option}
                                      value={option}
                                      control={<Radio />}
                                      label={option}
                                      disabled={readOnly}
                                    />
                                  ))}
                                </RadioGroup>
                              )}
                            </FormControl>
                          );
                        }
                        return null;
                      })}
                    </Stack>
                  </Box>
                );
              }

              // Skip fields that should be grouped under a heading when rendering independently
              if (field.groupUnder) {
                return null;
              }

              // Text field
              if (field.fieldType === 'text') {
                return (
                  <TextField
                    key={field.fieldId}
                    label={field.fieldLabel}
                    value={fieldValue}
                    onChange={(e) => handleFieldChange(field.fieldId, e.target.value)}
                    fullWidth
                    required={field.required}
                    disabled={field.readOnly || readOnly}
                    size="small"
                  />
                );
              }

              // Textarea field
              if (field.fieldType === 'textarea') {
                return (
                  <Box key={field.fieldId}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: 'text.primary' }}>
                      {field.fieldLabel}
                      {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
                    </Typography>
                    <TextField
                      value={fieldValue}
                      onChange={(e) => handleFieldChange(field.fieldId, e.target.value)}
                      fullWidth
                      required={field.required}
                      disabled={field.readOnly || readOnly}
                      multiline
                      rows={field.rows || 4}
                      size="small"
                      placeholder="Enter your response here..."
                    />
                  </Box>
                );
              }

              // Radio field
              if (field.fieldType === 'radio') {
                return (
                  <FormControl key={field.fieldId} component="fieldset" required={field.required}>
                    <FormLabel component="legend" sx={{ mb: 1, color: 'text.primary', fontWeight: 500 }}>
                      {field.fieldLabel}
                    </FormLabel>
                    {readOnly ? (
                      <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                        {fieldValue || 'No response'}
                      </Typography>
                    ) : (
                      <RadioGroup
                        value={fieldValue}
                        onChange={(e) => handleFieldChange(field.fieldId, e.target.value)}
                      >
                        {field.options?.map((option) => (
                          <FormControlLabel
                            key={option}
                            value={option}
                            control={<Radio />}
                            label={option}
                            disabled={readOnly}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  </FormControl>
                );
              }

              // Date field
              if (field.fieldType === 'date') {
                return (
                  <TextField
                    key={field.fieldId}
                    label={field.fieldLabel}
                    type="date"
                    value={fieldValue}
                    onChange={(e) => handleFieldChange(field.fieldId, e.target.value)}
                    fullWidth
                    required={field.required}
                    disabled={field.readOnly || readOnly}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                );
              }

              return null;
            })}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};

export default DynamicFormRenderer;
